import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Close, Download, Inventory, UploadFile } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { exportFile } from '../../../utils';
import useRecipeV2Api from '../hooks/useRecipeV2Api';
import type { RecipeBulkImportRow, RecipeBulkImportRun } from '../../../app/services/api/recipeV2';

interface BulkManageDialogProps {
  open: boolean;
  franchiseId: number;
  onClose: () => void;
  onSuccess: () => void;
}

const isFailure = (row: RecipeBulkImportRow) =>
  row.resultStatus === 'Invalid' || row.resultStatus === 'CommitFailed';

const recipeOperationRows = (run: RecipeBulkImportRun) => {
  const rows = run.rows.filter((row) =>
    row.sourceSection === 'Recipes' || row.sourceSection === 'Lifecycle');
  const seen = new Set<string>();
  return rows.filter((row) => {
    const key = `${row.recipeItemNumber ?? ''}|${row.operation ?? ''}`;
    if (!row.operation || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const BulkManageDialog = ({ open, franchiseId, onClose, onSuccess }: BulkManageDialogProps) => {
  const { t } = useTranslation('recipeV2');
  const api = useRecipeV2Api();
  const [file, setFile] = useState<File | null>(null);
  const [run, setRun] = useState<RecipeBulkImportRun | null>(null);
  const [busy, setBusy] = useState<'export' | 'validate' | 'commit' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState(() => uuidv4());

  useEffect(() => {
    if (!open) {
      setFile(null);
      setRun(null);
      setBusy(null);
      setError(null);
      setIdempotencyKey(uuidv4());
    }
  }, [open]);

  const failures = useMemo(() => run?.rows.filter(isFailure) ?? [], [run]);
  const operations = useMemo(() => run ? recipeOperationRows(run) : [], [run]);
  const canCommit = !!file && !!run && run.status === 'Validated' && failures.length === 0;
  const canRetry = !!file && !!run && run.status === 'PartiallyCommitted';

  const handleDownload = async () => {
    setBusy('export');
    setError(null);
    try {
      const workbook = await api.exportBulkWorkbook(franchiseId);
      exportFile(
        workbook,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        `Recipes-V2-${franchiseId}.xlsx`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : t('bulkManage.errorExporting'));
    } finally {
      setBusy(null);
    }
  };

  const handleFile = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0] ?? null;
    setError(null);
    setRun(null);
    if (selected && !selected.name.toLowerCase().endsWith('.xlsx')) {
      setFile(null);
      setError(t('bulkManage.errorInvalidFile'));
      return;
    }
    setFile(selected);
    setIdempotencyKey(uuidv4());
  };

  const handleValidate = async () => {
    if (!file) return;
    setBusy('validate');
    setError(null);
    try {
      setRun(await api.validateBulkWorkbook(franchiseId, idempotencyKey, file));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('bulkManage.errorUploading'));
    } finally {
      setBusy(null);
    }
  };

  const handleCommit = async () => {
    if (!file || !run) return;
    setBusy('commit');
    setError(null);
    try {
      const committed = await api.commitBulkWorkbook(run.id, franchiseId, file);
      setRun(committed);
      if (committed.status === 'Committed') onSuccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('bulkManage.errorCommit'));
      try {
        const current = await api.getBulkImportRun(run.id);
        setRun(current);
        if (current.status === 'Committed') onSuccess();
      } catch {
        // Preserve the last known run. The original commit error remains actionable.
      }
    } finally {
      setBusy(null);
    }
  };

  const groupedFailures = useMemo(() => {
    const groups = new Map<string, RecipeBulkImportRow[]>();
    failures.forEach((row) => {
      const key = `${row.sourceSection}|${row.errorCode ?? 'Validation'}`;
      groups.set(key, [...(groups.get(key) ?? []), row]);
    });
    return Array.from(groups.entries());
  }, [failures]);

  return (
    <Dialog open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="lg"
      fullWidth>
      <DialogTitle sx={{ position: 'relative', pr: 6 }}>
        <Stack direction="row"
          alignItems="center"
          spacing={1}>
          <Inventory fontSize="small" />
          <Typography variant="textXL">{t('bulkManage.title')}</Typography>
        </Stack>
        <IconButton
          onClick={onClose}
          disabled={!!busy}
          size="small"
          aria-label={t('bulkManage.close')}
          sx={{ position: 'absolute', top: 8, right: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          <Typography variant="textSM">{t('bulkManage.productionDescription')}</Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }}
            spacing={1}>
            <Button variant="secondary"
              startIcon={<Download />}
              onClick={handleDownload}
              disabled={!!busy}>
              {busy === 'export' ? t('bulkManage.exporting') : t('bulkManage.download')}
            </Button>
            <Button
              variant="secondary"
              startIcon={<UploadFile />}
              onClick={() => document.getElementById('recipe-v2-bulk-file')?.click()}
              disabled={!!busy}
            >
              {t('bulkManage.selectWorkbook')}
            </Button>
            <input
              id="recipe-v2-bulk-file"
              type="file"
              accept=".xlsx"
              aria-label={t('bulkManage.selectWorkbook')}
              onChange={handleFile}
              style={{ display: 'none' }}
            />
          </Stack>

          {file && (
            <Alert severity="info">
              {t('bulkManage.fileSelected', { name: file.name })} {t('bulkManage.validationDoesNotCommit')}
            </Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}

          {run && (
            <Box>
              <Stack direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 2 }}>
                <Typography variant="textLG"
                  fontWeight={600}>{t('bulkManage.runStatus')}</Typography>
                <Chip label={run.status}
                  size="small" />
                <Typography variant="textXS"
                  color="text.secondary">#{run.id}</Typography>
              </Stack>

              {operations.length > 0 && (
                <TableContainer sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('bulkManage.colRecipeSku')}</TableCell>
                        <TableCell>{t('bulkManage.operation')}</TableCell>
                        <TableCell>{t('bulkManage.result')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {operations.map((row) => (
                        <TableRow key={`${row.sourceSection}-${row.rowNumber}-${row.operation}`}>
                          <TableCell>{row.recipeItemNumber ?? '-'}</TableCell>
                          <TableCell><Chip label={row.operation ?? '-'}
                            size="small"
                            variant="outlined" /></TableCell>
                          <TableCell>{row.resultStatus}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {groupedFailures.map(([key, rows]) => {
                const [section, code] = key.split('|');
                return (
                  <Box key={key}
                    sx={{ mb: 3 }}>
                    <Typography variant="textSM"
                      fontWeight={600}
                      sx={{ mb: 1 }}>
                      {section} - {code} ({rows.length})
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('bulkManage.colRow')}</TableCell>
                            <TableCell>{t('bulkManage.colRecipeSku')}</TableCell>
                            <TableCell>{t('bulkManage.operation')}</TableCell>
                            <TableCell>{t('bulkManage.message')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.map((row) => (
                            <TableRow key={`${row.sourceSection}-${row.rowNumber}`}>
                              <TableCell>{row.rowNumber}</TableCell>
                              <TableCell>{row.recipeItemNumber ?? '-'}</TableCell>
                              <TableCell>{row.operation ?? '-'}</TableCell>
                              <TableCell>{row.errorMessage ?? row.errorCode ?? '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                );
              })}

              {run.status === 'Committed' && (
                <Alert severity="success">{t('bulkManage.commitSuccess')}</Alert>
              )}
              {run.status === 'PartiallyCommitted' && (
                <Alert severity="warning">{t('bulkManage.partialCommit')}</Alert>
              )}
            </Box>
          )}

          {busy && (
            <Stack direction="row"
              alignItems="center"
              spacing={1}>
              <CircularProgress size={18} />
              <Typography variant="textSM">
                {busy === 'validate' ? t('bulkManage.validating') : busy === 'commit' ? t('bulkManage.committing') : t('bulkManage.exporting')}
              </Typography>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="secondary"
          onClick={onClose}
          disabled={!!busy}>{t('bulkManage.close')}</Button>
        {file && (!run || run.status === 'ValidationFailed') && (
          <Button variant="primary"
            onClick={handleValidate}
            disabled={!!busy}>
            {t('bulkManage.validate')}
          </Button>
        )}
        {(canCommit || canRetry) && (
          <Button variant="primary"
            onClick={handleCommit}
            disabled={!!busy}>
            {canRetry ? t('bulkManage.retryCommit') : t('bulkManage.commit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkManageDialog;
